import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import {
  ModalAcceptButton,
  ModalCancelButton,
} from "../../../constants/component.constants";
import {
  ModalContainer,
  ModalInnerContainer,
  ResponsiveRow,
} from "../../../constants/layout.constants";
import { useEffect, useState } from "react";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  isEmptyString,
  isNullUndefined,
} from "../../../util/util";
import { setMessage } from "../../../actions/message";
import { useDispatch, useSelector } from "react-redux";
import { saveAppointment } from "../../../services/listingsServices/listing.service";
import { getIdTypes } from "../../../services/list.service";
import dayjs from "dayjs";
import { fetchPerson } from "../../../services/workOrder.service";

export default function ListingBookingModal(props) {
  const user = useSelector((state) => state.auth.user);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [appointmentDate, setAppointmentDate] = useState(null);
  const dispatch = useDispatch();
  const [idNo, setIdNo] = useState("");
  const [idTypes, setIdTypes] = useState([]);
  const [idType, setIdType] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [initialFormState, setInitialFormState] = useState({});
  const [formChanged, setFormChanged] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState("");

  // Check if we're in edit mode
  const isEditMode = props.action === "edit";

  useEffect(() => {
    if (!isNullUndefined(user)) {
      fetchPerson(user.userId).then((response) => {
        setFirstName(response?.firstName);
        setMiddleName(response?.middleName);
        setLastName(response?.lastName);
        setEmail(response?.email);
        setPhoneNo(response?.mobilePhone);
        setIdType(response?.idTypeCd);
        setIdNo(response?.idNumber);

        // If editing, store the initial state after setting values
        if (props.action === "edit" && props.booking?.id) {
          setInitialFormState({
            firstName: response?.firstName || '',
            middleName: response?.middleName || '',
            lastName: response?.lastName || '',
            email: response?.email || '',
            phoneNo: response?.mobilePhone || '',
            idType: response?.idTypeCd || '',
            idNo: response?.idNumber || '',
            appointmentDate: props.booking?.date || null,
            rescheduleReason: props.booking?.notes || ''
          });
          // Set the reschedule reason from existing booking notes
          setRescheduleReason(props.booking?.notes || '');
        }
      });
    }
    
    // If we're editing, set the booking ID and appointment date from props
    if (props.action === "edit" && props.booking?.id) {
      setBookingId(props.booking.id);
      setAppointmentDate(new Date(props.booking.date));
    }
    
    getIdTypes().then((data) => setIdTypes(data));
    setOpenModal(true);
  }, [user, props.action, props.booking]);

  const checkFormChanges = () => {
    if (props.action !== "edit") return true; // Always enable button if not editing

    const currentFormState = {
      firstName: firstName || '',
      middleName: middleName || '',
      lastName: lastName || '',
      email: email || '',
      phoneNo: phoneNo || '',
      idType: idType || '',
      idNo: idNo || '',
      appointmentDate: appointmentDate ? appointmentDate.toISOString() : null,
      rescheduleReason: rescheduleReason || ''
    };

    // Compare current state with initial state
    const hasChanges = Object.keys(currentFormState).some(key => {
      return currentFormState[key] !== initialFormState[key];
    });

    setFormChanged(hasChanges);
    return hasChanges;
  };

  const handleDateChange = (value) => {
    let date = new Date(value);
    setAppointmentDate(date);
    checkFormChanges();
  };

  const validateFormData = () => {
    // Only validate person details for new appointments
    if (!isEditMode) {
      if (isEmptyString(firstName)) {
        dispatch(
          setMessage({
            type: "warning",
            message: "First Name is required.",
          })
        );
        return false;
      }

      if (isEmptyString(lastName)) {
        dispatch(
          setMessage({
            type: "warning",
            message: "Last Name is required.",
          })
        );
        return false;
      }

      if (isEmptyString(phoneNo)) {
        dispatch(
          setMessage({
            type: "warning",
            message: "Phone Number is required.",
          })
        );
        return false;
      }

      if (isEmptyString(idType)) {
        dispatch(
          setMessage({
            type: "warning",
            message: "ID Type is required.",
          })
        );
        return false;
      }

      if (isEmptyString(idNo)) {
        dispatch(
          setMessage({
            type: "warning",
            message: "ID Number is required.",
          })
        );
        return false;
      }
    }

    // Always validate appointment date
    if (!appointmentDate) {
      dispatch(
        setMessage({
          type: "warning",
          message: "Appointment date is required.",
        })
      );
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    // Validate form data first before attempting to access appointmentDate
    if (!validateFormData()) {
      return;
    }

    let sendData;
    
    if (props.action === "edit") {
      // For rescheduling - only send essential fields
      sendData = {
        id: bookingId,
        listingId: props.listing.id,
        appointmentDate: appointmentDate.toISOString(true),
        rescheduleReason: rescheduleReason,
      };
    } else {
      // For new appointments - send all required fields
      sendData = {
        listingId: props.listing.id,
        firstName: firstName,
        middleName: middleName,
        lastName: lastName,
        email: email,
        phoneNumber: phoneNo,
        idNo: idNo,
        idType: idType,
        appointmentDate: appointmentDate?.toISOString(true),
      };
    }
    
    saveAppointment(sendData).then((response) => {
      if (!response) return

      if (props.action === "edit") {
        dispatch(
          setMessage({
            type: response.type,
            message: "Booking updated successfully",
          })
        );
      } else {
        dispatch(
          setMessage({
            type: response.type,
            message: response.message,
          })
        );
      }

      if (response.type !== "error") {
        closeModal();
        setTimeout(() => {
          props.refetchTourBooking();
        }, 100);
      }
    }).catch((error) => {
      dispatch(
        setMessage({
          type: "error",
          message: "Failed to update booking. Please try again.",
        })
      );
    });
  };

  const closeModal = () => {
    setAppointmentDate(null);
    setFirstName(null);
    setMiddleName(null);
    setLastName(null);
    setEmail(null);
    setPhoneNo(null);
    setIdType(null);
    setIdNo(null);
    setBookingId(null); // Reset booking ID
    setRescheduleReason(""); // Reset reschedule reason
    setOpenModal(false);
    props.closeModal();
  };

  return (
    <ModalContainer open={openModal} onClose={closeModal}>
      <ModalInnerContainer
        sx={{
          maxHeight: { xs: "50hv", lg: "90vh" },
          width: { xs: "80%", lg: "70%" },
        }}
      >
        <Typography variant="body1" sx={{ color: "#037171", fontWeight: 600 }}>
          Book Viewing Tour
        </Typography>
        <Typography
          variant="h2"
          sx={{ marginBottom: "20px", marginTop: "10px", fontWeight: 900 }}
        >
          {props.listing && props.listing.propertyName}
        </Typography>

        <ResponsiveRow
          sx={{
            width: "100%",
            justifyContent: { xs: "center", lg: "space-between" },
            marginTop: "20px",
          }}
        >
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "90%" },
                backgroundColor: "#efefef",
                borderRadius: "10px",
              }}
              label="First Name"
              required={true}
              disabled={isEditMode}
              value={firstName}
              onChange={(event) => {
                let value = event.target.value;
                setFirstName(value);
                checkFormChanges();
              }}
            />
          </Grid>{" "}
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "90%" },
                backgroundColor: "#efefef",
                borderRadius: "10px",
              }}
              label="Middle Name (Optional)"
              disabled={isEditMode}
              value={middleName}
              onChange={(event) => {
                let value = event.target.value;
                setMiddleName(value);
                checkFormChanges();
              }}
            />
          </Grid>{" "}
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "90%" },
                backgroundColor: "#efefef",
                borderRadius: "10px",
              }}
              label="Last Name"
              required={true}
              disabled={isEditMode}
              value={lastName}
              onChange={(event) => {
                let value = event.target.value;
                setLastName(value);
                checkFormChanges();
              }}
            />
          </Grid>{" "}
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "90%" },
                backgroundColor: "#efefef",
                borderRadius: "10px",
              }}
              label="Email"
              type="email"
              disabled={isEditMode}
              value={email}
              onChange={(event) => {
                let value = event.target.value;
                setEmail(value);
                checkFormChanges();
              }}
            />
          </Grid>{" "}
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "90%" },
                backgroundColor: "#efefef",
                borderRadius: "10px",
              }}
              label="Phone Number"
              type="tel"
              required={true}
              disabled={isEditMode}
              value={phoneNo}
              onChange={(event) => {
                let value = event.target.value;
                setPhoneNo(value);
                checkFormChanges();
              }}
            />
          </Grid>{" "}
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          ></Grid>
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "90%" },
                backgroundColor: "#efefef",
                borderRadius: "10px",
              }}
              select
              label="ID Type"
              required={true}
              disabled={isEditMode}
              value={idType}
              onChange={(event) => {
                let value = event.target.value;
                setIdType(value);
                checkFormChanges();
              }}
            >
              {idTypes &&
                idTypes.length > 0 &&
                idTypes.map(function (idType, i) {
                  return (
                    <MenuItem key={i} value={idType.value}>
                      {idType.label}
                    </MenuItem>
                  );
                }, this)}
            </TextField>
          </Grid>{" "}
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <TextField
              variant="outlined"
              sx={{
                width: { xs: "100%", lg: "90%" },
                backgroundColor: "#efefef",
                borderRadius: "10px",
              }}
              label="ID Number"
              required={true}
              disabled={isEditMode}
              value={idNo}
              onChange={(event) => {
                let value = event.target.value;
                setIdNo(value);
                checkFormChanges();
              }}
            />
          </Grid>{" "}
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          ></Grid>
          <Grid
            item
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DateTimePicker
                    sx={{ width: "90%" }}
                    id={"appointmentDate"}
                    value={dayjs(appointmentDate)}
                    onChange={(newDate) => handleDateChange(newDate)}
                    disablePast={true}
                    ampm={true}
                    slotProps={{ textField: { required: true }}}
                />
            </LocalizationProvider>
          </Grid>{" "}
          {/* Add reschedule reason field for edit mode */}
          {props.action === "edit" && (
            <Grid
              item
              sx={{ width: { xs: "100%", lg: "100%" }, marginBottom: "10px" }}
            >
              <TextField
                variant="outlined"
                sx={{
                  width: { xs: "100%", lg: "100%" },
                  backgroundColor: "#efefef",
                  borderRadius: "10px",
                }}
                label="Reschedule Reason (Optional)"
                multiline
                rows={3}
                value={rescheduleReason}
                onChange={(event) => {
                  let value = event.target.value;
                  setRescheduleReason(value);
                  checkFormChanges();
                }}
                placeholder="Please provide a reason for rescheduling your appointment..."
              />
            </Grid>
          )}
        </ResponsiveRow>
        <ResponsiveRow
          sx={{
            width: "100%",
            justifyContent: { xs: "center", lg: "flex-end" },
          }}
        >
          <ModalAcceptButton
            onClick={() => handleSubmit()}
            sx={{ width: { xs: "100%", lg: "200px" } }}
            disabled={props.action === "edit" && !formChanged}
          >
            Submit
          </ModalAcceptButton>
          <ModalCancelButton
            onClick={() => closeModal()}
            sx={{ width: { xs: "100%", lg: "200px" } }}
          >
            Cancel
          </ModalCancelButton>
        </ResponsiveRow>
      </ModalInnerContainer>
    </ModalContainer>
  );
}
